// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

/**
 * Measured telemetry for a set of logical nodes, aligned to a plan's time axis.
 *
 * One react-query per node rather than one for the set: each node can 404 or 409
 * on its own and the combined curve must still draw. The 20-request fan-out and
 * why it can't be a single upstream read: see ADR-0012.
 *
 * The cache entry is "this node's observations for this plan-day", so the window
 * end is a fetch parameter and deliberately **not** part of the key. Keying on it
 * made every poll a cold miss: `data` went undefined, the summed curve went
 * all-null, and the drawn line vanished for the length of a round trip once a
 * minute. Each poll now folds a tail window into the entry it already has
 * (see {@link mergeWindowedSeries}), so the curve extends instead of blinking.
 */

import { useMemo, useRef } from 'react';
import { keepPreviousData, useQueries, useQueryClient } from '@tanstack/react-query';
import { getLogicalNodeObservations, toMetricSeries } from '../services/telemetryService';
import { resampleToAxis, sumSeries } from '../utils/telemetryResample';
import { mergeWindowedSeries } from '../utils/telemetryMerge';
import { axisStartMs } from '../utils/planSeries';
import {
  deltaWindowStart,
  isServerFault,
  liveBackoffInterval,
  pollWindowEnd,
} from '../utils/pollWindow';
import type { WindowedMetricSeries } from '../utils/telemetryMerge';
import type { ResamplePoint } from '../utils/telemetryResample';
import type { MetricSeries, TimeAxis } from '../types';

/**
 * Pick the metric a card wants. Metric names come from upstream-authored
 * aggregation profiles and are not a contract this repo can pin, so match the
 * requested substrings in order, then fall back to a lone series as unambiguous.
 */
function pickMetric(series: MetricSeries[], match: string[]): MetricSeries | undefined {
  for (const needle of match) {
    const hit = series.find(s => s.name.toLowerCase().includes(needle.toLowerCase()));
    if (hit) return hit;
  }
  return series.length === 1 ? series[0] : undefined;
}

function toPoints(series: MetricSeries | undefined): ResamplePoint[] {
  return (series?.data ?? []).map(row => ({ timestamp: row.key, value: row.value }));
}

/** Why a node contributed nothing, in the words the card should use. */
export type NodeFailure = 'unresolved-membership' | 'not-found' | 'unavailable' | 'no-data';

export interface LiveNodeSeries {
  logicalNodeId: string;
  /** Axis-aligned values; `null` where nothing was measured. */
  values: (number | null)[];
  failure?: NodeFailure;
  /**
   * The latest poll failed but this node still has a cached series, which is
   * what `values` holds. The curve is drawn and correct as of `stale`'s last
   * success -- it is simply not advancing.
   */
  stale?: boolean;
}

export interface UseLiveLogicalNodeSeriesResult {
  /** One entry per requested node, in the order they were requested. */
  nodes: LiveNodeSeries[];
  /** Element-wise sum across the nodes that reported. */
  total: (number | null)[];
  /** How many nodes produced at least one measured value. */
  contributing: number;
  loading: boolean;
  /** Nodes whose membership upstream has never been resolved (409). */
  unresolved: string[];
  /** Nodes that failed for any other reason. */
  failed: string[];
  /**
   * Nodes still drawing a cached series because their latest poll failed. These
   * also count towards `contributing` -- the curve is on screen -- so a card that
   * wants to disclose the staleness has to read this rather than infer it.
   */
  stale: string[];
}

function classify(error: unknown): NodeFailure {
  const status = (error as { status?: unknown })?.status;
  if (status === 409) return 'unresolved-membership';
  if (status === 404) return 'not-found';
  return 'unavailable';
}

export interface LiveLogicalNodeSeriesOptions {
  /** Metric-name substrings to prefer, most specific first. */
  match?: string[];
}

/**
 * Observations for `logicalNodeIds` over the plan's axis so far, resampled onto
 * that axis.
 *
 * The window closes at now, rounded to the poll boundary: asking for the whole
 * axis would invite upstream to answer with future-dated buckets, and the point
 * of the Live curves is that they stop where measurement stops. It opens at the
 * axis start on the first read and at the tail of what is already cached on
 * every read after that.
 *
 * Note there is no `now` parameter. The poll drives freshness on its own, and
 * taking a ticking clock as an argument is what previously dragged the window
 * end into the cache key.
 */
export function useLiveLogicalNodeSeries(
  siteId: string | undefined,
  logicalNodeIds: string[],
  timeAxis: TimeAxis | undefined,
  { match = ['power'] }: LiveLogicalNodeSeriesOptions = {}
): UseLiveLogicalNodeSeriesResult {
  const queryClient = useQueryClient();
  const axisStart = timeAxis ? axisStartMs(timeAxis) : undefined;
  const start = axisStart !== undefined ? new Date(axisStart).toISOString() : undefined;
  const enabled = Boolean(siteId && start && timeAxis);

  // Consecutive server-fault polls per node: each node backs off on its own.
  const failures = useRef(new Map<string, number>());

  const results = useQueries({
    queries: logicalNodeIds.map(logicalNodeId => {
      const queryKey = ['telemetry', 'logical-node', siteId ?? '', logicalNodeId, start ?? ''];
      return {
        queryKey,
        queryFn: async () => {
          const previous = queryClient.getQueryData<WindowedMetricSeries>(queryKey);
          const end = pollWindowEnd(new Date());
          const from = deltaWindowStart(start as string, previous?.fetchedThrough);
          const failureKey = queryKey.join('|');
          try {
            const envelope = await getLogicalNodeObservations(siteId as string, logicalNodeId, {
              start: from,
              end,
            });
            failures.current.delete(failureKey);
            return mergeWindowedSeries(previous, toMetricSeries(envelope.data), {
              end,
              dropBefore: start as string,
            });
          } catch (error) {
            if (isServerFault(error)) {
              failures.current.set(failureKey, (failures.current.get(failureKey) ?? 0) + 1);
            }
            throw error;
          }
        },
        enabled,
        refetchInterval: () => liveBackoffInterval(failures.current.get(queryKey.join('|')) ?? 0),
        // Same-key refetch keeps `data` on its own; this covers the key actually
        // changing (site switch, plan rollover), where the curve would otherwise
        // drop to undefined and the line disappear mid-session.
        placeholderData: keepPreviousData,
        // No retry override needed: the client's shouldRetry skips every 4xx, so a
        // 404 or 409 surfaces immediately instead of burning a retry budget it
        // could never satisfy.
      };
    }),
  });

  const statuses = results.map(r => `${r.status}:${r.isFetching}:${r.dataUpdatedAt}`).join(',');

  return useMemo(() => {
    const nodes: LiveNodeSeries[] = logicalNodeIds.map((logicalNodeId, i) => {
      const result = results[i];
      if (result?.isError && !result.data) {
        return { logicalNodeId, values: [], failure: classify(result.error) };
      }
      const series = pickMetric(result?.data?.series ?? [], match);
      const values = timeAxis ? resampleToAxis(toPoints(series), timeAxis) : [];
      const measured = values.some(v => v !== null);
      return {
        logicalNodeId,
        values,
        failure: measured ? undefined : 'no-data',
        stale: result?.isError ? true : undefined,
      };
    });

    const reporting = nodes.filter(n => !n.failure);
    return {
      nodes,
      total: sumSeries(reporting.map(n => n.values)),
      contributing: reporting.length,
      loading: enabled && results.some(r => r.isPending),
      unresolved: nodes
        .filter(n => n.failure === 'unresolved-membership')
        .map(n => n.logicalNodeId),
      failed: nodes
        .filter(n => n.failure === 'not-found' || n.failure === 'unavailable')
        .map(n => n.logicalNodeId),
      stale: nodes.filter(n => n.stale).map(n => n.logicalNodeId),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statuses, logicalNodeIds.join(','), timeAxis, enabled, match.join(',')]);
}
